import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Check, ChevronRight, Sparkles, X, Pill, Stethoscope, Phone, Users, BellRing, Camera } from "lucide-react";

/**
 * First-session onboarding nudge.
 * Shows a checklist of 6 quick wins for new caregivers. Each step links to where
 * the action happens. Dismissible (persisted to user preferences). Auto-hides
 * once all are complete OR user explicitly dismisses.
 */
const STORAGE_KEY = "hc_onboarding_dismissed";

export default function OnboardingChecklist({ chests }) {
  const { user } = useAuth();
  const nav = useNavigate();
  const [progress, setProgress] = useState(null);
  const [dismissed, setDismissed] = useState(() => {
    try { return localStorage.getItem(STORAGE_KEY) === "1"; } catch { return false; }
  });

  const ownedChests = chests?.owned || [];
  const firstChest = ownedChests[0];

  useEffect(() => {
    if (!firstChest) { setProgress({}); return; }
    api.get(`/onboarding/progress?chest_id=${firstChest.id}`)
      .then((r) => setProgress(r.data))
      .catch(() => setProgress({}));
  }, [firstChest?.id]);

  if (dismissed || !user || progress === null) return null;

  // If they have no chest yet, show "Create your first Hope Chest" call to action above the empty state — handled elsewhere
  if (!firstChest) return null;

  const steps = [
    {
      key: "added_medication", icon: Pill,
      label: "Add their medications",
      hint: "Even just one — you can edit later.",
      done: progress.added_medication,
      go: () => nav(`/chest/${firstChest.id}?tab=medications`),
    },
    {
      key: "added_appointment", icon: Stethoscope,
      label: "Add a doctor's appointment",
      hint: "Past or upcoming, doesn't matter.",
      done: progress.added_appointment,
      go: () => nav(`/chest/${firstChest.id}?tab=appointments`),
    },
    {
      key: "added_contact", icon: Phone,
      label: "Add an emergency contact",
      hint: "The one person you call first.",
      done: progress.added_contact,
      go: () => nav(`/chest/${firstChest.id}?tab=contacts`),
    },
    {
      key: "invited_family", icon: Users,
      label: "Invite a family member",
      hint: "A sibling, spouse, or close friend.",
      done: progress.invited_family,
      premium: true,
      go: () => nav(`/chest/${firstChest.id}?tab=members`),
    },
    {
      key: "phone_verified", icon: BellRing,
      label: "Turn on SMS reminders",
      hint: "Get nudges for appointments and meds.",
      done: progress.phone_verified,
      premium: true,
      go: () => nav("/settings"),
    },
    {
      key: "first_photo", icon: Camera,
      label: "Share your first moment",
      hint: "A photo a day keeps the family close.",
      done: progress.first_photo,
      premium: true,
      go: () => nav(`/chest/${firstChest.id}?tab=photos`),
    },
  ];

  const completed = steps.filter((s) => s.done).length;
  const total = steps.length;
  const pct = Math.round((completed / total) * 100);

  // Auto-hide once fully complete
  if (completed === total) return null;

  const dismiss = () => {
    try { localStorage.setItem(STORAGE_KEY, "1"); } catch {}
    setDismissed(true);
  };

  return (
    <div className="hc-card p-6 mb-8 relative overflow-hidden bg-gradient-to-br from-[#F3EFEA] to-white border-[#E8DCC8]" data-testid="onboarding-checklist">
      <button
        onClick={dismiss}
        className="absolute top-4 right-4 p-1.5 rounded-full hover:bg-[#EBE5DF] text-[#7A736E]"
        title="Hide this checklist"
        data-testid="onboarding-dismiss"
      >
        <X className="w-4 h-4" />
      </button>
      <div className="flex items-center gap-2 mb-1">
        <Sparkles className="w-4 h-4 text-[#3A5A4A]" />
        <span className="text-xs uppercase tracking-[0.2em] text-[#7A736E]">Getting started</span>
      </div>
      <h2 className="font-display text-2xl mb-1">
        You're {pct}% set up.
      </h2>
      <p className="text-sm text-[#7A736E] mb-4">
        A few small steps make Hope Chest dramatically more useful. Each one takes 30 seconds or less.
      </p>

      <div className="h-1.5 rounded-full bg-[#EBE5DF] mb-5 overflow-hidden">
        <div
          className="h-full bg-gradient-to-r from-[#3A5A4A] to-[#C87B67] transition-all duration-500"
          style={{ width: `${pct}%` }}
        />
      </div>

      <div className="grid sm:grid-cols-2 gap-2.5">
        {steps.map((s) => {
          const Icon = s.icon;
          return (
            <button
              key={s.key}
              onClick={s.go}
              disabled={s.done}
              className={`flex items-start gap-3 p-3 rounded-xl text-left transition border ${
                s.done
                  ? "bg-[#D8E2D8]/40 border-[#D8E2D8] cursor-default"
                  : "bg-white border-[#EBE5DF] hover:border-[#3A5A4A] hover:shadow-sm cursor-pointer"
              }`}
              data-testid={`onboarding-step-${s.key}`}
            >
              <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${
                s.done ? "bg-[#3A5A4A] text-white" : "bg-[#F3EFEA] text-[#3A5A4A]"
              }`}>
                {s.done ? <Check className="w-4 h-4" /> : <Icon className="w-4 h-4" />}
              </div>
              <div className="flex-1 min-w-0">
                <p className={`text-sm font-medium ${s.done ? "text-[#7A736E] line-through" : "text-[#3D3935]"}`}>
                  {s.label}
                  {s.premium && !s.done && (
                    <span className="ml-2 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-[#E8DCC8] text-[#3A5A4A] font-medium">Premium</span>
                  )}
                </p>
                {!s.done && <p className="text-xs text-[#7A736E] mt-0.5">{s.hint}</p>}
              </div>
              {!s.done && <ChevronRight className="w-4 h-4 text-[#7A736E] flex-shrink-0 mt-2" />}
            </button>
          );
        })}
      </div>
    </div>
  );
}
